<template>
  <div class="bg-blue-5 h-screen flex">
    <div class="relative h-full left-0 hidden lg:inline-block">
      <img src="/signup-bg.jpg" class="h-full">
      <div class="absolute inset-0 bg-blue-100/70"></div>
      <div class="absolute inset-0 bg-gradient-to-b from-blue-100 via-orange-100/50 to-blue-100"></div>
      <div class="absolute left-5 top-5 text-white text-left p-10 pt-20">
        <img src="/troopers-white-logo.svg" alt="troopers logo" class="h-5">
        <h1 class="text-5xl font-bold mt-5">The smarter way to manage your workforce</h1>
        <p class="mt-10 font-light text-lg text-light-gray">Hire, manage, and pay workers — all in one place built for operations teams.</p>
        <ul class="text-lg mt-1 list-disc list-inside">
          <li>Flexible shift scheduling</li>
          <li>Real-time attendance tracking</li>
          <li>Automated payroll & claims</li>
        </ul>
      </div>
    </div>
    <div class="flex-1">
      <div class="w-full sm:w-sm mx-auto pt-5 text-md flex flex-col gap-4">
        <StepProgress :steps="['Account', 'Verify', 'Company']" :current-step="currentStep"  class="px-2 sm:px-0"/>
        <div v-if="!successfulSignup" class="bg-white w-full p-5 rounded-xl text-left shadow-sm">
          <div class="flex flex-col gap-4">
            <div>
              <h2 class="font-bold text-lg">Create your account</h2>
              <p class="text-dark-gray">Start with your login credentials</p>
            </div>
            <!-- phoneNumber -->
            <input-box title="Work email" :required="true" type="email" placeholder="Email address" v-model="form.email" class=""/>
            <input-box title="Password" :required="true" type="password" placeholder="Password" v-model="form.password" class=""/>
            <password-strength :password="form.password" v-model="passwordStrength" />
            <div>
              <input-box title="Confirm password" :required="true" type="password" placeholder="Re-enter password" v-model="form.confirmPassword" class=""/>
              <div v-if="passwordMismatch" class="text-xs text-red-110 text-left">Passwords do not match</div>
            </div>
            <input name="phoneNumber" type="number" v-model="phoneNumber" autocomplete="off" tabindex="-1" aria-hidden="true" style="position:absolute;left:-9999px;width:0;height:0;overflow:hidden;" />
            <btn-submit class="text-lg w-full" :enabled="canSubmit && !submitting" @clicked="triggerReCaptcha">{{ submitting ? 'Submitting ...' : 'Continue →' }}</btn-submit>
          </div>
        </div>
        <div v-else class="bg-white w-full p-8 rounded-xl text-center shadow-sm flex flex-col items-center gap-4">
          <div class="w-10 h-10 rounded-full bg-green-100/20 flex items-center justify-center text-green-100 text-2xl">
            ✔
          </div>
          <div>
            <h2 class="text-2xl font-bold">Check your inbox</h2>
            <p class="mt-2">We've sent a verification link. Open your email and click the link to continue.</p>
          </div>
          <div class="w-full bg-light-gray border-2 border-gray rounded-xl p-4 text-left">
            <p class="text-xs font-semibold text-mid-gray uppercase tracking-widest mb-1">Sent to</p>
            <p class="font-semibold">{{ maskedEmail }}</p>
            <p class="text-sm mt-2">
              <span v-if="resendCooldown > 0" class="text-mid-gray">Resend available in {{ resendCooldown }}s</span>
              <span v-else @click="triggerResend" class="text-orange-100 underline cursor-pointer">Didn't receive an email? Request again</span>
            </p>
          </div>
          <div class="w-full border-2 border-gray rounded-xl p-4 text-left">
            <p class="font-bold mb-1">What to do next</p>
            <p class="text-dark-gray text-sm">Use the most recent verification email link we have sent you. For security reasons, this link expires in 24 hours.</p>
            <p class="text-mid-gray text-sm mt-2">If it's not in your inbox, check spam or promotions.</p>
          </div>
        </div>
        <p v-if="!successfulSignup" class="text-md text-dark-gray">Already have an account? <router-link to="/login" class="text-orange-100 underline">Sign in</router-link></p>
      </div>
    </div>
    <GoogleRecaptcha ref="recaptcha" action="employer_signup" @verify="requestToSignup" />
  </div>
</template>

<script>
import StepProgress from '@/components/StepProgress.vue'
import InputBox from "@/components/InputBox.vue";
import PasswordStrength from "@/components/PasswordStrength.vue";
import GoogleRecaptcha from "@/components/GoogleRecaptcha.vue";

export default {
  name: "employer-signup",
  components: { StepProgress, InputBox, PasswordStrength, GoogleRecaptcha },
  data() {
    return {
      form: {
        email: null,
        password: null,
        confirmPassword: null,
      },
      phoneNumber: null, //honeypot
      passwordStrength: null,
      submitting: false,
      successfulSignup: false,
      currentStep: 1,
      resendCooldown: 0,
      _resendTimer: null,
    }
  },
  computed: {
    passwordMismatch() {
      return !!this.form.confirmPassword && this.form.confirmPassword !== this.form.password
    },
    canSubmit() {
      return this.form.email && this.form.password && this.form.confirmPassword === this.form.password && this.passwordStrength && this.passwordStrength !== "Weak"
    },
    maskedEmail() {
      const email = this.form.email || ''
      const [local, domain] = email.split('@')
      if (!domain || local.length <= 2) return email
      return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`
    },
  },
  watch: {
    successfulSignup(val) {
      if (val) this.startResendTimer(30)
    },
  },
  destroyed() {
    clearInterval(this._resendTimer)
  },
  methods: {
    startResendTimer(seconds) {
      clearInterval(this._resendTimer)
      this.resendCooldown = seconds
      this._resendTimer = setInterval(() => {
        this.resendCooldown--
        if (this.resendCooldown <= 0) clearInterval(this._resendTimer)
      }, 1000)
    },
    triggerResend() {
      this.startResendTimer(60)
      this.$refs.recaptcha.execute('employer-signup')
    },
    triggerReCaptcha() {
      this.$refs.recaptcha.execute('employer-signup')
    },
    async requestToSignup(token) {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      try {
        await this.$store.dispatch('requestToSignup', {
          email: this.form.email,
          password: this.form.password,
          phoneNumber: this.phoneNumber || undefined,
          token,
        })
        this.successfulSignup = true
        this.currentStep = 2
      } catch (error) {
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>